import z from 'zod';

const planLevelSchema = z.union([z.literal('Family'), z.literal('Individual')]);

const payerContactInformationSchema = z.object({
  phone: z.string().nullable().optional(),
  url: z.string().nullable().optional(),
});

const primaryCareProviderSchema = z.object({
  firstName: z.string(),
  lastName: z.string(),
  npi: z.string(),
  category: z.string(),
  address: z.unknown(),
  contactInformation: z.array(payerContactInformationSchema).nullable().optional(),
  lastVisitDate: z.string().nullable().optional(),
});

const amountsSchema = z.object({
  amount: z.number().nullable().optional(),
  total: z.number().nullable().optional(),
  remaining: z.number().nullable().optional(),
  level: planLevelSchema.nullable().optional(),
  units: z.string().nullable().optional(),
});

const benefitDetailSchema = z.object({
  inNetwork: z.array(amountsSchema).nullable().optional(),
  outOfNetwork: z.array(amountsSchema).nullable().optional(),
  notApplicableNetwork: z.array(amountsSchema).nullable().optional(),
  noNetwork: z.array(amountsSchema).nullable().optional(),
});

const benefitDetailAmountsSchema = z.object({
  coPayment: benefitDetailSchema.nullable().optional(),
  outOfPocket: benefitDetailSchema.nullable().optional(),
  deductibles: benefitDetailSchema.nullable().optional(),
  coInsurance: benefitDetailSchema.nullable().optional(),
});

export const coverageSnapshotSchema = z.object({
  payer: z.object({
    type: z.string().nullable().optional(),
    name: z.string(),
    contactInformation: z.array(payerContactInformationSchema).nullable().optional(),
  }),
  patient: z.object({
    lastName: z.string(),
    firstName: z.string(),
    dob: z.string(),
    subscriberRelationship: z.string(),
    gender: z.string(),
  }),
  subscriber: z.object({
    firstName: z.string(),
    lastName: z.string(),
    memberId: z.string(),
  }),
  plans: z.array(
    z.object({
      status: z.string(),
      statusCode: z.string(),
      groupNumber: z.string(),
      groupName: z.string(),
      eligibilityStartDate: z.string().nullable().optional(),
      eligibilityEndDate: z.string().nullable().optional(),
      planStartDate: z.string().nullable().optional(),
      planEndDate: z.string().nullable().optional(),
      primaryCareProvider: primaryCareProviderSchema.nullable().optional(),
      isActive: z.boolean(),
      accumulations: benefitDetailAmountsSchema.nullable().optional(),
    })
  ),
  asOfDate: z.string(),
});

export type CoverageSnapshot = z.infer<typeof coverageSnapshotSchema>;
export type BenefitDetailAmounts = z.infer<typeof benefitDetailAmountsSchema>;
export type Amounts = z.infer<typeof amountsSchema>;
export type PlanLevel = z.infer<typeof planLevelSchema>;
